import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { articles } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();

  const article = articles.find((a) => a.id === Number(id));
  const relatedArticles = articles
    .filter((a) => a.id !== Number(id) && a.category === article?.category)
    .slice(0, 2);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <i className="ri-article-line text-4xl text-foreground-300"></i>
            </div>
            <p className="text-foreground-600 mb-4">{t('noResults')}</p>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xs"></i>
              {t('backToHome')}
            </Link>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: t('articles'), to: '/article/1' },
              { label: article.title },
            ]}
          />

          <div className="mb-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent-100/80 text-accent-700">
                {article.category}
              </span>
              <span className="text-xs text-foreground-400">
                {t('publishedOn')} {article.date}
              </span>
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4 leading-snug">
              {article.title}
            </h1>

            <div className="flex items-center gap-2 flex-wrap mb-6">
              <span className="text-xs text-foreground-500">{t('tags')}：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-0.5 rounded-full text-xs bg-secondary-100/80 text-secondary-700"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          <div className="aspect-[21/9] rounded-lg overflow-hidden mb-8">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
              [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-8 [&_h2]:mb-4
              [&_p]:mb-4 [&_p]:leading-relaxed
              [&_strong]:text-foreground-900 [&_strong]:font-semibold
            "
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {relatedArticles.length > 0 && (
            <section className="mt-12 pt-8 border-t border-background-200/70">
              <h2 className="text-lg font-bold text-foreground-950 mb-5">
                {t('relatedArticles')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group p-4 rounded-lg bg-background-100 border border-background-200/60 hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-accent-100/80 text-accent-700 mb-2">
                      {ra.category}
                    </span>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1.5 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {ra.title}
                    </h3>
                    <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">
                      {ra.summary}
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: article.title,
              description: article.summary,
              datePublished: article.date,
              author: { '@type': 'Organization', name: '蓝色导航在线' },
              publisher: { '@type': 'Organization', name: '蓝色导航在线' },
              keywords: article.tags.join(', '),
            }),
          }}
        />
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}